/**
 * The refill invariant.
 *
 *     before + added = after
 *
 * This one equation is what the product's credibility rests on. It is what makes a
 * short delivery or a siphoned tank visible: if the truck says it put in 6,000 liters
 * and the tank only rose by 4,200, the arithmetic refuses to close and somebody has to
 * explain the difference.
 *
 * The server enforces it (it is a domain invariant, and the API answers 422 with
 * `validation_errors` when it is broken). This module is the SAME rule, computed as the
 * operator types, so an impossible refill is never sent in the first place. The UI check
 * is not a substitute for the server's — it is the fast half of the same truth.
 *
 * Deliberately pure: no React, no fetch, no i18n. It returns codes; the UI localizes them.
 */

export type RefillField = "before_liters" | "added_liters" | "after_liters" | "working_hours";

export type RefillIssueCode =
  | "inconsistent_total" // before + added ≠ after — the headline invariant
  | "over_capacity" // after > the tank's capacity: fuel does not fit in the tank
  | "hours_backwards" // an hour meter cannot run backwards
  | "negative" // liters and hours are physical quantities
  | "not_a_number";

export interface RefillIssue {
  field: RefillField;
  code: RefillIssueCode;
}

export interface RefillDraft {
  before_liters: number | null;
  added_liters: number | null;
  after_liters: number | null;
  working_hours: number | null;
}

export interface RefillContext {
  /** The tank's capacity. `after` may never exceed it. */
  capacityLiters: number;
  /**
   * The selected generator's current meter reading, when one is selected. `working_hours`
   * may not be below it. Undefined when no generator is attributed to the refill.
   */
  generatorRunningHours?: number;
}

export interface RefillCheck {
  /** Safe to send to the server. False means at least one invariant is broken. */
  valid: boolean;
  /** Every broken rule. A field can appear more than once. */
  issues: RefillIssue[];
  /** `before + added` — what `after` MUST be. Null when either input is missing. */
  expectedAfter: number | null;
  /** True only when all three numbers are present and the equation closes. */
  balances: boolean;
  /** Resulting fill as a fraction of capacity, for the sight glass. Null when unknown. */
  fillFraction: number | null;
}

/**
 * Liters are entered to at most one decimal place, but they arrive here as IEEE doubles,
 * where 1850.1 + 6000.2 is not exactly 7850.3. Comparing with `===` would reject arithmetic
 * that is correct to every human looking at it. A tenth of a liter is far below the
 * resolution of any dipstick, so it is the right place to draw the line.
 */
const EPSILON = 0.05;

const isNumber = (v: number | null): v is number => v !== null && Number.isFinite(v);

export function checkRefill(draft: RefillDraft, context: RefillContext): RefillCheck {
  const issues: RefillIssue[] = [];
  const { before_liters, added_liters, after_liters, working_hours } = draft;

  const fields: Array<[RefillField, number | null]> = [
    ["before_liters", before_liters],
    ["added_liters", added_liters],
    ["after_liters", after_liters],
    ["working_hours", working_hours],
  ];

  for (const [field, value] of fields) {
    if (value === null) continue; // absent is "not filled in yet", not "invalid"
    if (!Number.isFinite(value)) {
      issues.push({ field, code: "not_a_number" });
      continue;
    }
    if (value < 0) issues.push({ field, code: "negative" });
  }

  const expectedAfter =
    isNumber(before_liters) && isNumber(added_liters) ? before_liters + added_liters : null;

  let balances = false;
  if (expectedAfter !== null && isNumber(after_liters)) {
    balances = Math.abs(expectedAfter - after_liters) <= EPSILON;
    if (!balances) {
      // The mismatch is reported on `after_liters` — that is the field the server names,
      // and it is the number the operator is being asked to justify.
      issues.push({ field: "after_liters", code: "inconsistent_total" });
    }
  }

  // A tank cannot hold more than it holds. Checked against `after` — and against the
  // expected total too, so the error appears even while `after` is still being typed.
  const effectiveAfter = isNumber(after_liters) ? after_liters : expectedAfter;
  if (
    effectiveAfter !== null &&
    context.capacityLiters > 0 &&
    effectiveAfter - context.capacityLiters > EPSILON
  ) {
    issues.push({ field: "after_liters", code: "over_capacity" });
  }

  // Hour meters do not run backwards. If they appear to, someone mistyped — or swapped
  // the generator, which is a different record, not this one.
  if (
    isNumber(working_hours) &&
    context.generatorRunningHours !== undefined &&
    working_hours < context.generatorRunningHours - EPSILON
  ) {
    issues.push({ field: "working_hours", code: "hours_backwards" });
  }

  const fillFraction =
    effectiveAfter !== null && context.capacityLiters > 0
      ? Math.min(Math.max(effectiveAfter / context.capacityLiters, 0), 1)
      : null;

  return {
    valid: issues.length === 0,
    issues,
    expectedAfter,
    balances,
    fillFraction,
  };
}

/** The first issue on a given field — one message per input; three is noise. */
export function issueFor(check: RefillCheck, field: RefillField): RefillIssue | undefined {
  return check.issues.find((i) => i.field === field);
}

/**
 * Is the draft complete enough to submit? Every required field present AND every rule met.
 * `added_liters: 0` is legitimate and must pass: a truck that arrived and put nothing in
 * the tank is precisely the fact a short-delivery investigation needs recorded.
 */
export function isSubmittable(draft: RefillDraft, context: RefillContext): boolean {
  const complete =
    isNumber(draft.before_liters) &&
    isNumber(draft.added_liters) &&
    isNumber(draft.after_liters) &&
    isNumber(draft.working_hours);
  if (!complete) return false;
  return checkRefill(draft, context).valid;
}
